import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationItems } from '../../mock';
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Plane,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Package,
  CreditCard,
  Receipt,
  Tag,
  Bell,
  Video,
  Shield,
  BookOpen,
  FileQuestion,
  GraduationCap,
  Compass,
  FileText,
  Building2,
  Handshake,
  Presentation,
  Megaphone,
  Wallet,
  Smartphone
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { LucideIcon } from 'lucide-react';
import { Separator } from '../ui/separator';

const iconMap: Record<string, LucideIcon> = {
  layoutDashboard: LayoutDashboard,
  users: Users,
  shoppingCart: ShoppingCart,
  plane: Plane,
  barChart3: BarChart3,
  settings: Settings,
  package: Package,
  creditCard: CreditCard,
  receipt: Receipt,
  tag: Tag,
  bell: Bell,
  video: Video,
  shield: Shield,
  bookOpen: BookOpen,
  fileQuestion: FileQuestion,
  graduationCap: GraduationCap,
  compass: Compass,
  fileText: FileText,
  building2: Building2,
  handshake: Handshake,
  presentation: Presentation,
  megaphone: Megaphone,
  wallet: Wallet,
  smartphone: Smartphone
};

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  isDark: boolean;
}

export default function Sidebar({ collapsed, setCollapsed, isDark }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen z-40 transition-all duration-300 ease-in-out flex flex-col',
        collapsed ? 'w-20' : 'w-64',
        isDark ? 'bg-[#0F172A] border-r border-slate-700' : 'bg-white border-r border-slate-200 shadow-sm'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'h-16 flex items-center px-4 border-b',
        isDark ? 'border-slate-700' : 'border-slate-200'
      )}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0EA5A4] to-[#0EA5A4]/70 flex items-center justify-center shadow-lg shadow-[#0EA5A4]/20">
            <span className="text-white font-bold text-lg">T</span>
          </div>
          {!collapsed && (
            <span className={cn(
              'font-semibold text-lg tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]'
            )}>
              Talaba UZ Admin
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = iconMap[item.icon];
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const topDivider = item.dividerType === 'top';
            const bottomDivider = item.dividerType === 'bottom';
            return (
              <li key={item.id}>
                {topDivider && <Separator className="my-2" />}
                <button
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group',
                    collapsed ? 'justify-center' : '',
                    isActive 
                      ? 'bg-[#0EA5A4] text-white shadow-lg shadow-[#0EA5A4]/30' 
                      : isDark 
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]'
                  )}
                >
                  <Icon className={cn(
                    'w-5 h-5 flex-shrink-0',
                    isActive ? 'text-white' : ''
                  )} />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.label}</span>
                  )}
                </button>
                {bottomDivider && <Separator className="my-2" />}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse button */}
      <div className={cn(
        'p-3 border-t',
        isDark ? 'border-slate-700' : 'border-slate-200'
      )}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl transition-all duration-200',
            isDark 
              ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
              : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]'
          )}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

